import { ApolloQueryResult } from 'apollo-client';

import {
    getValueFromApiFormat,
    mapCampaignRowPlatformToCampaignPlatform,
    mapCampaignRowTypeToCampaignType,
    mapDateRangePresetToApiFormat,
    mapSortParamsToString,
    mapTotalStatsToApiFormat
} from '../../mappers/common/common';
import {
    mapPhraseFilterToApiFormat,
    mapRowsToPhrases
} from '../../mappers/phrase/phrase';
import { getDefaultSort } from '../../service/GridHelpers/GridHelpers';
import { ClientInfo } from '../../typings/common';
import {
    GdStatRequirementsInput,
    PhrasesQuery,
    PhrasesSuggestQuery
} from '../../typings/graphql-grid';
import {
    defaultClientInfo,
    GridParams,
    GridSourceParams
} from '../../typings/grid';

export const mapSourceParamsToPhrasesAndSuggestQueryVariables = (
    params: GridParams,
    clientInfo: ClientInfo
) => {
    const {
        campaignsIds,
        groupsIds,
        sortParams,
        statPeriod,
        filter,
        view,
        goalIds,
        campaignsCalcTypeFilter
    } = params;

    const apiFormatFilter = mapPhraseFilterToApiFormat(
        clientInfo,
        filter,
        campaignsCalcTypeFilter
    );
    let statRequirements: GdStatRequirementsInput = {
        goalIds
    };
    if (statPeriod) {
        const { statPreset, statFrom, statTo } = statPeriod;
        statRequirements = {
            ...statRequirements,
            preset: statPreset && mapDateRangePresetToApiFormat(statPreset),
            from: statFrom,
            to: statTo
        };
    }

    return {
        orderBy: sortParams
            ? mapSortParamsToString(sortParams)
            : getDefaultSort(view),
        statRequirements,
        filter: {
            ...apiFormatFilter,
            campaignIdIn: campaignsIds,
            adGroupIdIn:
                groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapPhraseQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<PhrasesQuery>,
    params: GridSourceParams
) => {
    const { client, reqId } = result.data;
    const {
        showConditions: phrasesResult,
        features,
        info: { workCurrency, countryRegionId }
    } = client;
    const {
        rowset: phrasesRows,
        totalCount,
        showConditionIds,
        features: { commonCampaignTypes, commonPlatform },
        cacheKey,
        totalStats
    } = phrasesResult;
    const { cost, clicks, shows, ctr } = totalStats;

    return {
        data: {
            data: mapRowsToPhrases(phrasesRows, client, params, features),
            total: totalCount,
            allItemsIds: showConditionIds.map(String),
            cacheKey: getValueFromApiFormat(cacheKey)
        },
        clientInfo: {
            ...defaultClientInfo,
            ...features,
            workCurrency,
            countryRegionId
        },
        phraseContextData: {
            commonPlatform: commonPlatform.map(
                mapCampaignRowPlatformToCampaignPlatform
            ),
            commonCampaignType: commonCampaignTypes.map(
                mapCampaignRowTypeToCampaignType
            )
        },
        totalStats: mapTotalStatsToApiFormat({
            cost,
            clicks,
            shows,
            ctr
        }),
        hasObjectsOverLimit: phrasesResult.hasObjectsOverLimit,
        reqId
    };
};

export const mapPhraseSuggestQueryResultToSuggestQueryResponse = (
    result: ApolloQueryResult<PhrasesSuggestQuery>
) => {
    return {
        data: result.data.client.showConditions.rowset.reduce(
            (res: string[], item) => {
                if (item.__typename === 'GdKeyword') {
                    res.push(item.keyword);
                }

                return res;
            },
            []
        ),
        reqId: result.data.reqId
    };
};
